import { get, isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

import FacetFilter from 'pages/Bulletin/components/DataTable/components/FacetFilter';
import Pagination from 'pages/Bulletin/components/Pagination';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from 'pages/Bulletin/constants';

import SortedIcon from './assets/sorted.svg';
import UnsortedIcon from './assets/unsorted.svg';
import useStyles from './styles';

interface Props<T> {
    data: T[];
    columns: ColumnDef<T>[];
    className?: string;
}

function DataTable<T>({ data, columns, className }: Props<T>) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: DEFAULT_PAGE_SIZE
    });
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
        columns.reduce<ColumnFiltersState>((output, column) => {
            if (column.enableColumnFilter) {
                const id = column.id || column.header?.toString();
                const value = get(column, ['meta', 'options'], []);
                if (id) {
                    output.push({ id, value });
                }
            }
            return output;
        }, [])
    );

    const { classes, cx } = useStyles();

    const table = useReactTable({
        data,
        columns,
        defaultColumn: {
            size: 0,
            enableColumnFilter: false
        },
        enableSortingRemoval: false,
        renderFallbackValue: '–',
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        state: {
            sorting,
            pagination,
            columnFilters
        }
    });

    const minWidth = useMemo(
        () =>
            table
                .getAllColumns()
                .reduce((output, { columnDef }) => output + (columnDef.size || columnDef.minSize || 0), 0),
        [table]
    );

    if (isEmpty(data)) {
        return null;
    }

    return (
        <div className={cx(classes.shape, className)}>
            <TableContainer className={classes.container}>
                <Table className={classes.table} style={{ minWidth }}>
                    <TableHead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map(({ id, column, isPlaceholder, getContext }) => {
                                    if (isPlaceholder) {
                                        return null;
                                    }

                                    const label = (
                                        <TableSortLabel
                                            hideSortIcon={!column.getCanSort()}
                                            disabled={!column.getCanSort()}
                                            IconComponent={column.getIsSorted() ? SortedIcon : UnsortedIcon}
                                            active={column.getCanSort()}
                                            direction={column.getIsSorted() || undefined}
                                            onClick={column.getToggleSortingHandler()}
                                            className={classes.sort}
                                        >
                                            {flexRender(column.columnDef.header, getContext())}
                                        </TableSortLabel>
                                    );

                                    return (
                                        <TableCell
                                            key={id}
                                            className={classes.th}
                                            style={{ width: column.columnDef.size || 'auto' }}
                                        >
                                            {column.getCanFilter() ? (
                                                <FacetFilter column={column} label={label} />
                                            ) : (
                                                label
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id} className={classes.td}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Pagination
                    className={classes.pagination}
                    hidden={data.length < Math.min(...PAGE_SIZES)}
                    count={table.getFilteredRowModel().rows.length}
                    page={table.getState().pagination.pageIndex}
                    pageSize={table.getState().pagination.pageSize}
                    pageOptions={PAGE_SIZES}
                    onPageChange={table.setPageIndex}
                    onPageSizeChange={table.setPageSize}
                />
            </TableContainer>
        </div>
    );
}

export default DataTable;
